import React, { useState, useEffect, useRef, useCallback, useDeferredValue } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, WMSTileLayer, CircleMarker, useMap } from 'react-leaflet';
import PotenzialPopup from '../components/PotenzialPopup';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PotenzialKunde, PotenzialImportLog, PotenzialStatus, UpdatePotenzialKundeRequest, PotenzialExportModus } from '../types';
import { potenzialService } from '../services/potenzialService';
import { useAuth } from '../contexts/AuthContext';
import 'leaflet/dist/leaflet.css';
import { Icon, Spinner, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, filterAktiv, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';

const STATUS_LABEL: Record<PotenzialStatus, string> = {
  NEU: 'Neu',
  KONTAKTIERT: 'Kontaktiert',
  ABGELEHNT: 'Abgelehnt',
  UMGEWANDELT: 'Kunde',
};

const STATUS_COLOR: Record<PotenzialStatus, string> = {
  NEU: 'bg-blue-100 text-blue-800',
  KONTAKTIERT: 'bg-yellow-100 text-yellow-800',
  ABGELEHNT: 'bg-red-100 text-red-800',
  UMGEWANDELT: 'bg-green-100 text-green-800',
};

function ScoreBalken({ score }: { score: number }) {
  const color = score >= 65 ? '#27ae60' : score >= 55 ? '#2ecc71' : score >= 45 ? '#f1c40f' : score >= 30 ? '#e67e22' : '#e74c3c';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-semibold w-10 text-right">{score.toFixed(0)}%</span>
    </div>
  );
}

function norm(v: number | undefined, min: number, max: number): number {
  if (v == null) return 0;
  return Math.max(0, Math.min(1, (v - min) / (max - min)));
}

function mittelwert(items: PotenzialKunde[]): number {
  if (items.length === 0) return 0;
  return items.reduce((s, i) => s + i.scoreProzent, 0) / items.length;
}

/** Gruppiert ein Array unter Beibehaltung der Eingabe-Reihenfolge (Sortierung). */
function bucket<T>(arr: T[], key: (t: T) => string): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const el of arr) {
    const k = key(el);
    const b = m.get(k);
    if (b) b.push(el);
    else m.set(k, [el]);
  }
  return Array.from(m.entries());
}

function GruppenScore({ avg }: { avg: number }) {
  const color = avg >= 65 ? '#27ae60' : avg >= 55 ? '#2ecc71' : avg >= 45 ? '#f1c40f' : avg >= 30 ? '#e67e22' : '#e74c3c';
  return (
    <span className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
      ⌀ {avg.toFixed(0)} %
    </span>
  );
}

interface KartenFokusZiel {
  punkte: [number, number][];
}

/** Setzt den Kartenausschnitt auf das übergebene Fokus-Ziel (ein Punkt oder eine Gruppe). */
function KartenFokus({ ziel }: { ziel: KartenFokusZiel | null }) {
  const map = useMap();
  useEffect(() => {
    if (!ziel || ziel.punkte.length === 0) return;
    if (ziel.punkte.length === 1) {
      map.flyTo(ziel.punkte[0], 18, { duration: 0.8 });
    } else {
      map.flyToBounds(ziel.punkte, { padding: [60, 60], maxZoom: 17, duration: 0.8 });
    }
  }, [ziel, map]);
  return null;
}

/**
 * Rechteckauswahl per Shift+Ziehen. Spannt ein Auswahlrechteck auf (Shift verhindert
 * Konflikte mit dem Karten-Verschieben und dem Einzel-Klick) und meldet die Bounds.
 * boxZoom wird ausgeschaltet, damit Shift+Ziehen nicht zoomt.
 */
function RechteckAuswahl({ aktiv, onFertig }: { aktiv: boolean; onFertig: (bounds: L.LatLngBounds) => void }) {
  const map = useMap();
  useEffect(() => {
    if (!aktiv) return;
    const container = map.getContainer();
    const boxZoomWar = map.boxZoom.enabled();
    map.boxZoom.disable();
    let start: L.Point | null = null;
    let box: HTMLDivElement | null = null;

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0 || !e.shiftKey) return;
      start = map.mouseEventToContainerPoint(e);
      box = L.DomUtil.create('div', '', container);
      Object.assign(box.style, {
        position: 'absolute', border: '2px dashed #163056',
        background: 'rgba(22,48,86,0.12)', zIndex: '800', pointerEvents: 'none',
        left: `${start.x}px`, top: `${start.y}px`, width: '0', height: '0',
      });
      L.DomEvent.preventDefault(e);
      L.DomEvent.stopPropagation(e);
    };
    const onMove = (e: MouseEvent) => {
      if (!start || !box) return;
      const p = map.mouseEventToContainerPoint(e);
      Object.assign(box.style, {
        left: `${Math.min(p.x, start.x)}px`, top: `${Math.min(p.y, start.y)}px`,
        width: `${Math.abs(p.x - start.x)}px`, height: `${Math.abs(p.y - start.y)}px`,
      });
    };
    const onUp = (e: MouseEvent) => {
      if (!start) return;
      const p = map.mouseEventToContainerPoint(e);
      const moved = start.distanceTo(p) >= 5;
      const bounds = L.latLngBounds(map.containerPointToLatLng(start), map.containerPointToLatLng(p));
      if (box) { L.DomUtil.remove(box); box = null; }
      start = null;
      if (moved) onFertig(bounds);
    };

    container.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      container.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (box) L.DomUtil.remove(box);
      if (boxZoomWar) map.boxZoom.enable();
    };
  }, [aktiv, map, onFertig]);
  return null;
}

/**
 * Lässt Leaflet seine Größe neu berechnen, wenn sich das Layout ändert (z. B. Karten-Vollbild
 * an/aus). Ohne invalidateSize bleiben sonst graue Kacheln im neu freigewordenen Bereich.
 */
function KartenGroesse({ trigger }: { trigger: unknown }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, [trigger, map]);
  return null;
}

/** Extrahiert gültige [lat, lon]-Paare aus einer Liste von Potenzialen. */
function koordinaten(items: PotenzialKunde[]): [number, number][] {
  return items
    .filter(i => i.lat != null && i.lon != null)
    .map(i => [Number(i.lat), Number(i.lon)] as [number, number]);
}

/** Kompakter "Auf Karte zeigen"-Button für Gruppenköpfe. */
function KarteButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      title="Auf Karte zeigen"
      className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800 whitespace-nowrap">
      <Icon.Karte className="w-4 h-4" />
      Karte
    </button>
  );
}

/** Kompakter "Als PDF exportieren"-Button für Gruppenköpfe. */
function ExportGruppeButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      title="Diese Gruppe als PDF exportieren"
      className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800 whitespace-nowrap">
      <Icon.Download className="w-4 h-4" />
      PDF
    </button>
  );
}

function ScoreBreakdown({ item }: { item: PotenzialKunde }) {
  const criteria = [
    { label: 'Freifläche', weight: 30, score: norm(item.flaecheFreiM2, 150, 1000), hint: '150–1000 m²' },
    { label: 'Bodenrichtwert', weight: 30, score: norm(item.bodenrichtwert, 100, 1500), hint: '100–1500 €/m²' },
    { label: 'Grundstück', weight: 20, score: norm(item.flaecheGrundstueckM2, 400, 3000), hint: '400–3000 m²' },
    { label: 'Gebäudegröße', weight: 20, score: norm(item.hauptgebaeude_m2, 80, 200), hint: '80–200 m²' },
  ];

  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">Score-Aufschlüsselung</div>
      <div className="space-y-2">
        {criteria.map(({ label, weight, score, hint }) => {
          const contribution = score * weight;
          const color = score >= 0.7 ? '#27ae60' : score >= 0.4 ? '#f1c40f' : '#e74c3c';
          return (
            <div key={label}>
              <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                <span>{label} <span className="text-gray-400">({hint})</span></span>
                <span className="font-medium">{contribution.toFixed(1)} / {weight} Pkt.</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${score * 100}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailModal({ item, onClose, onStatusChange, onDelete, onShowOnMap }: {
  item: PotenzialKunde;
  onClose: () => void;
  onStatusChange: (id: number, req: UpdatePotenzialKundeRequest) => void;
  onDelete: (id: number) => void;
  onShowOnMap: (item: PotenzialKunde) => void;
}) {
  const [status, setStatus] = useState<PotenzialStatus>(item.status);
  const [notiz, setNotiz] = useState(item.notiz ?? '');
  const [saving, setSaving] = useState(false);

  const adresse = [item.strasse, item.hausnummer].filter(Boolean).join(' ');
  const adresseZeile2 = [item.plz, item.ort].filter(Boolean).join(' ');
  const mapsUrl = item.lat && item.lon
    ? `https://www.google.com/maps?q=${item.lat},${item.lon}`
    : null;

  async function handleSave() {
    setSaving(true);
    try {
      await onStatusChange(item.id, { status, notiz: notiz || undefined });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ui-fade fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2000 }} onClick={onClose}>
      <div className="ui-pop bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {adresse || 'Adresse unbekannt'}
          </h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600"><Icon.Close className="h-5 w-5" /></button>
        </div>

        {/* Inhalt */}
        <div className="p-5 space-y-4">
          {/* Score */}
          <div>
            <div className="text-xs text-gray-500 mb-1">Pool-Potenzial Score</div>
            <ScoreBalken score={item.scoreProzent} />
          </div>

          {/* Adresse + Maps */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Adresse</div>
              <div className="font-medium">{adresse}</div>
              <div className="text-gray-600">{adresseZeile2}</div>
              {item.gemeinde && <div className="text-gray-500 text-xs mt-0.5">Gemeinde: {item.gemeinde}</div>}
            </div>
            {mapsUrl && (
              <div className="flex flex-col items-start justify-end gap-1.5">
                <button
                  onClick={() => onShowOnMap(item)}
                  className="flex items-center gap-1 text-brand-600 hover:underline text-xs">
                  <Icon.Karte className="w-4 h-4" />
                  Auf Karte zeigen
                </button>
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-brand-600 hover:underline text-xs">
                  <Icon.MapPin className="w-4 h-4" />
                  Google Maps
                </a>
              </div>
            )}
          </div>

          {/* Kennzahlen */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Freifläche', value: item.flaecheFreiM2 ? `${Math.round(item.flaecheFreiM2)} m²` : '' },
              { label: 'Grundstück', value: item.flaecheGrundstueckM2 ? `${Math.round(item.flaecheGrundstueckM2)} m²` : '' },
              { label: 'Bodenrichtwert', value: item.bodenrichtwert ? `${Math.round(item.bodenrichtwert)} €/m²` : '' },
              { label: 'Gebäudegröße', value: item.hauptgebaeude_m2 ? `${Math.round(item.hauptgebaeude_m2)} m²` : '' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="font-semibold text-gray-900 text-sm mt-0.5">{value}</div>
              </div>
            ))}
          </div>

          {/* Score-Aufschlüsselung */}
          <ScoreBreakdown item={item} />

          {/* Status */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as PotenzialStatus)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              {(Object.keys(STATUS_LABEL) as PotenzialStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>

          {/* Notiz */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notiz</label>
            <textarea
              value={notiz}
              onChange={e => setNotiz(e.target.value)}
              rows={3}
              placeholder="Interne Notiz …"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-5 border-t">
          <button
            onClick={() => { if (window.confirm(`Potenzial „${adresse || 'Adresse unbekannt'}“ löschen?`)) onDelete(item.id); }}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50">
            Löschen
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Wird gespeichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

const EXPORT_TOP_N = 500;

/** Auswahl-Dialog für den PDF-Export der Potenzialliste. */
function ExportModal({ items, onClose, onExport }: {
  items: PotenzialKunde[];
  onClose: () => void;
  onExport: (modus: PotenzialExportModus, anzahl: number) => Promise<void>;
}) {
  const [modus, setModus] = useState<PotenzialExportModus>('TOP');
  const [anzahl, setAnzahl] = useState(Math.min(EXPORT_TOP_N, items.length));
  const [busy, setBusy] = useState(false);

  const gemeindeCount = new Set(items.map(i => i.gemeinde?.trim() || 'Ohne Gemeinde')).size;
  const plzCount = new Set(items.map(i => i.plz?.trim() || 'Ohne PLZ')).size;
  const ortCount = new Set(items.map(i => i.ort?.trim() || 'Ohne Ort')).size;

  const optionen: { key: PotenzialExportModus; titel: string; beschreibung: string }[] = [
    { key: 'TOP', titel: 'Beste nach Score', beschreibung: 'Die höchstbewerteten Adressen, fortlaufend nummeriert.' },
    { key: 'GEMEINDE', titel: 'Gruppiert nach Gemeinde', beschreibung: `${items.length} Adressen in ${gemeindeCount} Gemeinde${gemeindeCount === 1 ? '' : 'n'}.` },
    { key: 'PLZ', titel: 'Gruppiert nach PLZ', beschreibung: `${items.length} Adressen in ${plzCount} PLZ-Gebiet${plzCount === 1 ? '' : 'en'}.` },
    { key: 'ORT', titel: 'Gruppiert nach Ort', beschreibung: `${items.length} Adressen in ${ortCount} Ort${ortCount === 1 ? '' : 'en'}.` },
  ];

  const anzahlOk = modus !== 'TOP' || (anzahl >= 1 && anzahl <= items.length);

  async function handle() {
    setBusy(true);
    try {
      await onExport(modus, anzahl);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ui-fade fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2000 }} onClick={onClose}>
      <div className="ui-pop bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Liste als PDF exportieren</h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600"><Icon.Close className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          {optionen.map(opt => (
            <label key={opt.key}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                modus === opt.key ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:bg-gray-50'
              }`}>
              <input type="radio" name="export-modus" value={opt.key}
                checked={modus === opt.key} onChange={() => setModus(opt.key)}
                className="mt-1 accent-brand-600" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm">{opt.titel}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.beschreibung}</div>
                {opt.key === 'TOP' && modus === 'TOP' && (
                  <div className="flex items-center gap-2 mt-2" onClick={e => e.preventDefault()}>
                    <input
                      type="number" min={1} max={items.length} value={anzahl}
                      onChange={e => setAnzahl(Math.max(1, Math.min(items.length, Number(e.target.value) || 1)))}
                      className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />
                    <span className="text-xs text-gray-400">von {items.length} verfügbar</span>
                  </div>
                )}
              </div>
            </label>
          ))}
          <p className="text-xs text-gray-400 leading-relaxed">
            Jede Zeile hat eine Abhak-Box. Oben steht ein QR-Code – einscannen öffnet die
            Google-Maps-Navigation zur ersten Adresse der Liste auf dem Handy.
          </p>
        </div>

        <div className="flex gap-2 p-5 border-t">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            Abbrechen
          </button>
          <button onClick={handle} disabled={busy || items.length === 0 || !anzahlOk}
            className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {busy ? 'PDF wird erstellt…' : 'PDF erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modal mit dem Import-Verlauf (nur Admin). */
function ImportLogModal({ log, onClose }: { log: PotenzialImportLog[]; onClose: () => void }) {
  return (
    <div className="ui-fade fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2000 }} onClick={onClose}>
      <div className="ui-pop bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Import-Verlauf</h2>
          <button onClick={onClose} aria-label="Schließen" className="text-gray-400 hover:text-gray-600"><Icon.Close className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {log.length === 0 ? (
            <EmptyState icon={<Icon.Upload />} title="Noch keine Importe" className="!py-6" />
          ) : (
            <table className="w-full text-xs text-gray-600">
              <thead>
                <tr className="text-left border-b text-gray-500">
                  <th className="pb-2 pr-6 font-semibold">Zeitpunkt</th>
                  <th className="pb-2 pr-6 font-medium">Benutzer</th>
                  <th className="pb-2 pr-6 font-medium">Neu importiert</th>
                  <th className="pb-2 font-medium">Bereits vorhanden</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {log.map(eintrag => (
                  <tr key={eintrag.id}>
                    <td className="py-2 pr-6">
                      {new Date(eintrag.importiertAm).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="py-2 pr-6">{eintrag.importiertVon}</td>
                    <td className="py-2 pr-6">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                        +{eintrag.importiert}
                      </span>
                    </td>
                    <td className="py-2">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                        {eintrag.uebersprungen} übersprungen
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Potenziale() {
  const [items, setItems] = useState<PotenzialKunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [karteSichtbar, setKarteSichtbar] = useState(false);
  const [karteVollbild, setKarteVollbild] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PotenzialKunde | null>(null);
  const [statusFilter, setStatusFilter] = useState<PotenzialStatus | 'ALLE'>('ALLE');
  const [minScore, setMinScore] = useState(0);
  // Verzögerter Score-Wert: die teure Filterung/Gruppierung läuft auf diesem Wert in
  // einer Low-Priority-Render, sodass das Eingabefeld flüssig bleibt. Hinkt der angezeigte
  // Wert hinterher, zeigen wir eine Lade-Animation.
  const deferredMinScore = useDeferredValue(minScore);
  const filterLaeuft = minScore !== deferredMinScore;
  const [suche, setSuche] = useState('');
  const [importing, setImporting] = useState(false);
  const [satellit, setSatellit] = useState(false);
  const [alkis, setAlkis] = useState(false);
  const [gemeinden, setGemeinden] = useState(false);
  const [importLog, setImportLog] = useState<PotenzialImportLog[]>([]);
  const [importLogOpen, setImportLogOpen] = useState(false);
  const { isAdmin } = useAuth();
  const [sortKey, setSortKey] = useState<'scoreProzent' | 'strasse' | 'flaecheFreiM2' | 'bodenrichtwert'>('scoreProzent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [gruppierung, setGruppierung] = useState<'keine' | 'plzOrt' | 'ortPlz' | 'gemeindeOrt'>('gemeindeOrt');
  const [gruppenSort, setGruppenSort] = useState<'name-asc' | 'name-desc' | 'kandidaten' | 'untergruppen' | 'score'>('name-asc');
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [kartenFokus, setKartenFokus] = useState<KartenFokusZiel | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [markierModus, setMarkierModus] = useState(false);
  const [markiert, setMarkiert] = useState<Set<number>>(new Set());
  const [exportLaeuft, setExportLaeuft] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Aus der Suche (`/potenziale?id=12`): nach dem Laden gleich das Detail öffnen.
  const sprungId = Number(searchParams.get('id')) || 0;
  useEffect(() => {
    if (!sprungId || items.length === 0) return;
    setSearchParams({}, { replace: true });
    const ziel = items.find((i) => i.id === sprungId);
    if (ziel) setSelectedItem(ziel);
  }, [sprungId, items, setSearchParams]);

  useEffect(() => {
    laden();
    if (isAdmin()) potenzialService.getImportLog().then(setImportLog).catch(() => {});
    // Nur beim Öffnen der Seite; die Rolle wechselt nicht, während sie offen ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function laden() {
    setLoading(true);
    try {
      setItems(await potenzialService.getAll());
    } catch (err) {
      toast.error(fehlerText(err, 'Potenziale konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await potenzialService.importGeoJson(file);
      const msg = result.uebersprungen > 0
        ? `${result.importiert} neu importiert, ${result.uebersprungen} bereits vorhanden`
        : `${result.importiert} Potenziale importiert`;
      toast.success(msg);
      if (isAdmin()) potenzialService.getImportLog().then(setImportLog).catch(() => {});
      await laden();
    } catch (err) {
      toast.error(fehlerText(err, 'GeoJSON konnte nicht importiert werden'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDelete(id: number) {
    try {
      await potenzialService.delete(id);
      setItems(prev => prev.filter(i => i.id !== id));
      setSelectedItem(null);
      toast.success('Potenzial gelöscht');
    } catch (err) {
      toast.error(fehlerText(err, 'Potenzial konnte nicht gelöscht werden'));
    }
  }

  async function handleDeleteAll() {
    if (!window.confirm(`Alle ${items.length} Potenziale löschen? Das lässt sich nicht rückgängig machen.`)) return;
    try {
      const anzahl = await potenzialService.deleteAll();
      setItems([]);
      setSelectedItem(null);
      toast.success(`${anzahl} Potenziale gelöscht`);
    } catch (err) {
      toast.error(fehlerText(err, 'Potenziale konnten nicht gelöscht werden'));
    }
  }

  async function handleStatusChange(id: number, req: UpdatePotenzialKundeRequest) {
    try {
      const updated = await potenzialService.updateStatus(id, req);
      markiereNeu(`potenzial-${id}`);
      setItems(prev => prev.map(i => i.id === id ? updated : i));
      toast.success('Potenzial gespeichert');
    } catch (err) {
      toast.error(fehlerText(err, 'Potenzial konnte nicht gespeichert werden'));
    }
  }

  const gefiltert = React.useMemo(() => items.filter(i => {
    if (statusFilter !== 'ALLE' && i.status !== statusFilter) return false;
    if (i.scoreProzent < deferredMinScore) return false;
    if (suche) {
      const s = suche.toLowerCase();
      return [i.strasse, i.hausnummer, i.plz, i.ort, i.gemeinde, i.label].some(f => f?.toLowerCase().includes(s));
    }
    return true;
  }), [items, statusFilter, deferredMinScore, suche]);
  const filterIstAktiv = suche.trim() !== '' || statusFilter !== 'ALLE' || minScore !== 0;
  const filterZuruecksetzen = () => { setSuche(''); setStatusFilter('ALLE'); setMinScore(0); };

  // Aktuelle gefilterte Liste in einem Ref spiegeln, damit die Rechteckauswahl-Callback
  // stabil bleibt und die Karten-Eventhandler nicht bei jedem Render neu gebunden werden.
  const gefiltertRef = useRef<PotenzialKunde[]>([]);
  gefiltertRef.current = gefiltert;

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'strasse' ? 'asc' : 'desc');
    }
  }

  const sortiert = React.useMemo(() => [...gefiltert].sort((a, b) => {
    let av: any = a[sortKey] ?? (sortKey === 'strasse' ? 'zzz' : -1);
    let bv: any = b[sortKey] ?? (sortKey === 'strasse' ? 'zzz' : -1);
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  }), [gefiltert, sortKey, sortDir]);

  // Ohne Blättern: die Liste wächst beim Scrollen stückweise nach.
  const { sichtbar: seiteDaten, Ende: listenEnde } = useStueckweise(sortiert);

  // Zweistufige örtliche Gruppierung. Je nach Modus bündelt eine obere Ebene
  // (PLZ, Ort oder Gemeinde) und die untere gliedert darunter. Läuft auf der bereits
  // gefilterten + sortierten Liste, daher bleibt die Sortierung innerhalb der Gruppen erhalten.
  const keyGemeinde = (i: PotenzialKunde) => i.gemeinde?.trim() || 'Ohne Gemeinde';
  const keyOrt = (i: PotenzialKunde) => i.ort?.trim() || 'Ohne Ort';
  const keyPlz = (i: PotenzialKunde) => i.plz?.trim() || 'Ohne PLZ';
  const wortOrt = (n: number) => (n === 1 ? 'Ort' : 'Orte');

  const obenKeyOf =
    gruppierung === 'ortPlz' ? keyOrt : gruppierung === 'gemeindeOrt' ? keyGemeinde : keyPlz;
  const untenKeyOf = gruppierung === 'ortPlz' ? keyPlz : keyOrt;
  // Beschriftungen je nach oberer Ebene.
  const obenLabel = gruppierung === 'ortPlz' ? 'Ort' : gruppierung === 'gemeindeOrt' ? 'Gemeinde' : 'PLZ';
  const untenWort = (n: number) => (gruppierung === 'ortPlz' ? 'PLZ' : wortOrt(n));
  const obenExportModus: PotenzialExportModus =
    gruppierung === 'ortPlz' ? 'ORT' : gruppierung === 'gemeindeOrt' ? 'GEMEINDE' : 'PLZ';
  const untenExportModus: PotenzialExportModus = gruppierung === 'ortPlz' ? 'PLZ' : 'ORT';

  const gruppen = bucket(sortiert, obenKeyOf)
    .map(([oben, obenItems]) => ({
      oben,
      count: obenItems.length,
      avg: mittelwert(obenItems),
      items: obenItems,
      unterGruppen: bucket(obenItems, untenKeyOf)
        .map(([unten, untenItems]) => ({ unten, items: untenItems, avg: mittelwert(untenItems) }))
        .sort((a, b) => a.unten.localeCompare(b.unten, 'de', { numeric: true })),
    }))
    .sort((a, b) => {
      const nameCmp = a.oben.localeCompare(b.oben, 'de', { numeric: true });
      switch (gruppenSort) {
        case 'name-desc': return -nameCmp;
        case 'kandidaten': return b.count - a.count || nameCmp;
        case 'untergruppen': return b.unterGruppen.length - a.unterGruppen.length || nameCmp;
        case 'score': return b.avg - a.avg || nameCmp;
        case 'name-asc':
        default: return nameCmp;
      }
    });

  const alleGruppenKeys = gruppen.flatMap(g => [g.oben, ...g.unterGruppen.map(p => `${g.oben}|||${p.unten}`)]);
  const alleOffen = alleGruppenKeys.length > 0 && alleGruppenKeys.every(k => offen.has(k));

  function toggleGruppe(key: string) {
    setOffen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAlle() {
    setOffen(alleOffen ? new Set() : new Set(alleGruppenKeys));
  }

  /** Blendet die Karte ein und setzt den Ausschnitt auf die übergebenen Potenziale. */
  function zeigeAufKarte(items: PotenzialKunde[]) {
    const punkte = koordinaten(items);
    if (punkte.length === 0) {
      toast.error('Keine Koordinaten vorhanden');
      return;
    }
    setKartenFokus({ punkte });
    setKarteSichtbar(true);
  }

  function toggleKarte() {
    setKarteSichtbar(v => !v);
    setKartenFokus(null);
    setKarteVollbild(false);
  }

  /** Lädt das Export-PDF für eine konkrete Adressauswahl herunter. */
  async function exportAuswahl(auswahl: PotenzialKunde[], modus: PotenzialExportModus) {
    const ids = auswahl.map(i => i.id);
    if (ids.length === 0) {
      toast.error('Keine Adressen für den Export vorhanden');
      return;
    }
    try {
      await potenzialService.exportPdf(modus, ids);
      toast.success('PDF wird heruntergeladen');
    } catch (err) {
      toast.error(fehlerText(err, 'PDF konnte nicht exportiert werden'));
    }
  }

  /** Schaltet ein Potenzial in der Kartenauswahl an/aus. */
  function toggleMarkiert(id: number) {
    setMarkiert(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Fügt alle (gefilterten) Potenziale innerhalb des aufgezogenen Rechtecks zur Auswahl hinzu. */
  const markiereImRechteck = useCallback((bounds: L.LatLngBounds) => {
    setMarkiert(prev => {
      const next = new Set(prev);
      for (const i of gefiltertRef.current) {
        if (i.lat != null && i.lon != null && bounds.contains([Number(i.lat), Number(i.lon)])) {
          next.add(i.id);
        }
      }
      return next;
    });
  }, []);

  /** Exportiert die auf der Karte markierten Potenziale als PDF (nummeriert nach Score). */
  async function exportMarkiert() {
    const auswahl = items.filter(i => markiert.has(i.id));
    setExportLaeuft(true);
    try {
      await exportAuswahl(auswahl, 'TOP');
    } finally {
      setExportLaeuft(false);
    }
  }

  /** Export aus dem Dialog: nimmt die aktuell gefilterten Potenziale nach Score. */
  async function handleExport(modus: PotenzialExportModus, anzahl: number) {
    const nachScore = [...gefiltert].sort((a, b) => b.scoreProzent - a.scoreProzent);
    const auswahl = modus === 'TOP' ? nachScore.slice(0, anzahl) : nachScore;
    await exportAuswahl(auswahl, modus);
  }

  function renderZeile(item: PotenzialKunde, eingerueckt = false) {
    const a = ankunft(`potenzial-${item.id}`);
    return (
      <tr key={item.id} ref={a.ref} onClick={() => setSelectedItem(item)}
        className={`hover:bg-gray-50 cursor-pointer transition-colors ${a.className}`}>
        <td className={`py-3 ${eingerueckt ? 'pl-12 pr-4' : 'px-4'}`}>
          <div className="font-semibold text-gray-900">
            {[item.strasse, item.hausnummer].filter(Boolean).join(' ') || <span className="text-gray-400 italic">Adresse unbekannt</span>}
          </div>
          <div className="text-gray-500 text-xs">{[item.plz, item.ort].filter(Boolean).join(' ')}</div>
        </td>
        <td className="px-4 py-3 w-36">
          <ScoreBalken score={item.scoreProzent} />
        </td>
        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
          {item.flaecheFreiM2 ? `${Math.round(item.flaecheFreiM2)} m²` : ''}
        </td>
        <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
          {item.bodenrichtwert ? `${Math.round(item.bodenrichtwert)} €/m²` : ''}
        </td>
        <td className="px-4 py-3">
          <span key={a.className ? item.status : undefined} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[item.status]} ${a.className ? 'ui-wechsel' : ''}`}>
            {STATUS_LABEL[item.status]}
          </span>
        </td>
        <td className="px-2 py-3 text-right">
          <button
            onClick={e => { e.stopPropagation(); if (window.confirm(`Potenzial „${[item.strasse, item.hausnummer].filter(Boolean).join(' ') || 'Adresse unbekannt'}“ löschen?`)) handleDelete(item.id); }}
            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
            title="Potenzial löschen" aria-label="Potenzial löschen">
            <Icon.Trash className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  }

  const mapCenter: [number, number] = items.length > 0 && items[0].lat && items[0].lon
    ? [Number(items[0].lat), Number(items[0].lon)]
    : [52.425, 9.836];

  return (
    <div className="seite-randlos flex flex-col h-full">
      {/* Kopfzeile */}
      <div className="px-6 py-4 border-b bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-gray-900">Potenzialanalyse</h1>
            <p className="text-sm text-gray-500 mt-1">{items.length} Kandidaten</p>
          </div>
          {isAdmin() && (
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" accept=".geojson,.json" className="hidden" onChange={handleImport} />
              <button
                onClick={() => setImportLogOpen(true)}
                title="Import-Verlauf anzeigen"
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                <Icon.Uhr className="w-4 h-4" />
                Import-Verlauf
                {importLog.length > 0 && (
                  <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                    {importLog.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
              >
                <Icon.Upload className="w-4 h-4" />
                {importing ? 'Wird importiert…' : 'GeoJSON importieren'}
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={items.length === 0}
                title="Alle Potenziale löschen"
                className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon.Trash className="w-4 h-4" />
                Alle löschen
              </button>
            </div>
          )}
        </div>

        {/* Steuerung + Filter */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mt-4">
          <button
            onClick={toggleKarte}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              karteSichtbar
                ? 'bg-brand-600 text-white border-brand-600 hover:bg-brand-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}>
            <Icon.Karte className="w-4 h-4" />
            {karteSichtbar ? 'Karte ausblenden' : 'Karte einblenden'}
          </button>
          <div className="relative flex-1 sm:min-w-[14rem]">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${suche ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text" placeholder="Adresse suchen …"
              value={suche} onChange={e => setSuche(e.target.value)}
              className={`w-full border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${suche ? filterAktiv : ''}`}
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}
            className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 ${statusFilter !== 'ALLE' ? filterAktiv : ''}`}>
            <option value="ALLE">Alle Status</option>
            {(Object.keys(STATUS_LABEL) as PotenzialStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <div className="flex items-center gap-1.5 whitespace-nowrap" title="Nur Potenziale mit mindestens diesem Score anzeigen">
            <span className="text-sm text-gray-600">Score ≥</span>
            <input
              type="number" min={0} max={100} step={5} value={minScore}
              onChange={e => setMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              className={`w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${minScore !== 0 ? filterAktiv : ''}`} />
            <span className="text-sm text-gray-400">%</span>
            {filterLaeuft && (
              <span role="status" aria-label="Filtere …" className="text-brand-600"><Spinner className="w-4 h-4" /></span>
            )}
          </div>
          <select value={gruppierung} onChange={e => setGruppierung(e.target.value as 'keine' | 'plzOrt' | 'ortPlz' | 'gemeindeOrt')}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500">
            <option value="keine">Ohne Gruppierung</option>
            <option value="gemeindeOrt">Gruppieren: Gemeinde, dann Ort</option>
            <option value="plzOrt">Gruppieren: PLZ, dann Ort</option>
            <option value="ortPlz">Gruppieren: Ort, dann PLZ</option>
          </select>
          {gruppierung !== 'keine' && (
            <select value={gruppenSort} onChange={e => setGruppenSort(e.target.value as typeof gruppenSort)}
              title="Reihenfolge der Gruppen"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500">
              <option value="name-asc">{obenLabel} aufsteigend</option>
              <option value="name-desc">{obenLabel} absteigend</option>
              <option value="kandidaten">Meiste Kandidaten</option>
              <option value="untergruppen">Meiste {gruppierung === 'ortPlz' ? 'PLZ' : 'Orte'}</option>
              <option value="score">Höchster Ø-Score</option>
            </select>
          )}
        </div>
      </div>

      {/* Inhalt */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <LoadingScreen />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon.Ziel />}
            title="Noch keine Potenziale importiert"
            hint={isAdmin() ? 'Importieren Sie eine GeoJSON-Datei mit Kandidaten.' : 'Ein Administrator kann Kandidaten per GeoJSON importieren.'}
            action={isAdmin() ? (
              <button onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700">
                <Icon.Upload className="h-4 w-4" />GeoJSON importieren
              </button>
            ) : undefined}
          />
        ) : (
          <div className="flex flex-col lg:flex-row h-full">
            {/* Liste — bei aktiver Karte unter lg ausgeblendet; im Karten-Vollbild komplett versteckt */}
            <div className={`${karteVollbild ? 'hidden' : karteSichtbar ? 'hidden lg:flex' : 'flex'} flex-col flex-1 min-h-0 min-w-0`}>
            <div className="flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  {[
                    { key: 'strasse', label: 'Adresse', cls: '' },
                    { key: 'scoreProzent', label: 'Score', cls: 'w-36' },
                    { key: 'flaecheFreiM2', label: 'Freifläche', cls: 'hidden md:table-cell' },
                    { key: 'bodenrichtwert', label: 'Bodenrichtwert', cls: 'hidden lg:table-cell' },
                  ].map(({ key, label, cls }) => (
                    <th key={key}
                      onClick={() => toggleSort(key as typeof sortKey)}
                      className={`text-left px-4 py-3 text-xs font-semibold text-gray-500 cursor-pointer select-none hover:text-gray-900 ${cls}`}
                      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
                      <span className="flex items-center gap-1">
                        {label}
                        {sortKey === key && (sortDir === 'asc'
                          ? <Icon.ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                          : <Icon.ChevronDown className="h-3.5 w-3.5 text-gray-500" />)}
                      </span>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gruppierung === 'keine'
                  ? seiteDaten.map(item => renderZeile(item))
                  : gruppen.map(g => {
                      const obenOffen = offen.has(g.oben);
                      return (
                        <React.Fragment key={g.oben}>
                          <tr onClick={() => toggleGruppe(g.oben)}
                            className="bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors">
                            <td colSpan={6} className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400 w-3.5">{obenOffen ? <Icon.ChevronDown className="h-3.5 w-3.5" /> : <Icon.ChevronRight className="h-3.5 w-3.5" />}</span>
                                <span className="font-semibold text-gray-900">{g.oben}</span>
                                <span className="text-xs text-gray-500">
                                  {g.count} {g.count === 1 ? 'Kandidat' : 'Kandidaten'} · {g.unterGruppen.length} {untenWort(g.unterGruppen.length)}
                                </span>
                                <div className="ml-auto flex items-center gap-3">
                                  <GruppenScore avg={g.avg} />
                                  <KarteButton onClick={e => { e.stopPropagation(); zeigeAufKarte(g.items); }} />
                                  <ExportGruppeButton onClick={e => { e.stopPropagation(); exportAuswahl(g.items, obenExportModus); }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                          {obenOffen && g.unterGruppen.map(p => {
                            const untenKey = `${g.oben}|||${p.unten}`;
                            const untenOffen = offen.has(untenKey);
                            return (
                              <React.Fragment key={untenKey}>
                                <tr onClick={() => toggleGruppe(untenKey)}
                                  className="bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                                  <td colSpan={6} className="px-4 py-1.5">
                                    <div className="flex items-center gap-2 pl-6">
                                      <span className="text-gray-400 w-3.5">{untenOffen ? <Icon.ChevronDown className="h-3.5 w-3.5" /> : <Icon.ChevronRight className="h-3.5 w-3.5" />}</span>
                                      <span className="font-medium text-gray-700 text-sm">{p.unten}</span>
                                      <span className="text-xs text-gray-400">
                                        {p.items.length} {p.items.length === 1 ? 'Kandidat' : 'Kandidaten'}
                                      </span>
                                      <div className="ml-auto flex items-center gap-3">
                                        <GruppenScore avg={p.avg} />
                                        <KarteButton onClick={e => { e.stopPropagation(); zeigeAufKarte(p.items); }} />
                                        <ExportGruppeButton onClick={e => { e.stopPropagation(); exportAuswahl(p.items, untenExportModus); }} />
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                {untenOffen && p.items.map(item => renderZeile(item, true))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
            {gruppierung === 'keine' && listenEnde}
            {sortiert.length === 0 && (
              <EmptyState icon={<Icon.Search />} title="Keine Potenziale gefunden" hint="Kein Kandidat passt zu den Filtern."
                action={filterIstAktiv ? <FilterZuruecksetzen onClick={filterZuruecksetzen} /> : undefined} />
            )}
            </div>
            {/* Fußzeile */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-white text-sm text-gray-600 shrink-0">
              {gruppierung !== 'keine' ? (
                <>
                  <span className="flex flex-wrap items-center gap-x-2">
                    <FilterStand treffer={sortiert.length} gesamt={items.length} einheit="Kandidaten" aktiv={filterIstAktiv} onZuruecksetzen={filterZuruecksetzen} />
                    <span>in {gruppen.length}{' '}
                    {gruppierung === 'ortPlz'
                      ? `Ort${gruppen.length === 1 ? '' : 'e'}`
                      : gruppierung === 'gemeindeOrt'
                        ? `Gemeinde${gruppen.length === 1 ? '' : 'n'}`
                        : `PLZ-Gebiet${gruppen.length === 1 ? '' : 'en'}`}
                    </span>
                  </span>
                  <button onClick={toggleAlle}
                    className="px-3 py-1 rounded text-brand-600 font-medium hover:bg-gray-100">
                    {alleOffen ? 'Alle zuklappen' : 'Alle aufklappen'}
                  </button>
                </>
              ) : (
                <>
                  <FilterStand treffer={sortiert.length} gesamt={items.length} einheit="Kandidaten" aktiv={filterIstAktiv} onZuruecksetzen={filterZuruecksetzen} />
                </>
              )}
            </div>
            </div>
            {karteSichtbar && (
              <div className="relative flex-1 min-h-0 min-w-0 lg:border-l border-gray-200">
                <div className="absolute top-3 right-3 flex items-center gap-2" style={{ zIndex: 1100 }}>
                  <button onClick={() => setKarteVollbild(v => !v)}
                    title={karteVollbild ? 'Liste wieder einblenden' : 'Karte maximieren (Liste ausblenden)'}
                    className="hidden lg:flex p-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 shadow-md hover:bg-gray-50 transition-colors">
                    {karteVollbild ? <Icon.Menue className="w-5 h-5" /> : <Icon.Vollbild className="w-5 h-5" />}
                  </button>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 text-sm font-medium shadow-md">
                    <button onClick={() => setSatellit(false)}
                      className={`px-3 py-1.5 transition-colors ${!satellit ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                      Karte
                    </button>
                    <button onClick={() => setSatellit(true)}
                      className={`px-3 py-1.5 border-l border-gray-300 transition-colors ${satellit ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                      Satellit
                    </button>
                  </div>
                  <button onClick={() => setGemeinden(v => !v)}
                    title="Gemeindegrenzen (© LGLN) ein-/ausblenden"
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium shadow-md transition-colors ${
                      gemeinden ? 'bg-brand-700 text-white border-brand-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}>
                    Gemeinden
                  </button>
                  <button onClick={() => setAlkis(v => !v)}
                    title="Flurstücksgrenzen (ALKIS, © LGLN) ein-/ausblenden"
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium shadow-md transition-colors ${
                      alkis ? 'bg-brand-700 text-white border-brand-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}>
                    Flurstücke
                  </button>
                  <button onClick={() => setMarkierModus(v => !v)}
                    title="Potenziale per Klick auf der Karte markieren und gesammelt exportieren"
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium shadow-md transition-colors ${
                      markierModus ? 'bg-brand-700 text-white border-brand-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}>
                    {markierModus ? 'Markieren beenden' : 'Markieren'}
                  </button>
                </div>
                {markierModus && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-2.5"
                    style={{ zIndex: 1100 }}>
                    <span className="text-sm text-gray-700">
                      <strong className="text-gray-900">{markiert.size}</strong> markiert
                    </span>
                    <span className="hidden sm:inline text-xs text-gray-400 whitespace-nowrap">
                      Klick = einzeln · Shift+ziehen = Rechteck
                    </span>
                    <button
                      onClick={() => setMarkiert(new Set(gefiltert.filter(i => i.lat && i.lon).map(i => i.id)))}
                      className="text-xs font-medium text-brand-600 hover:text-brand-800 whitespace-nowrap">
                      Alle sichtbaren
                    </button>
                    <button
                      onClick={() => setMarkiert(new Set())}
                      disabled={markiert.size === 0}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-40 whitespace-nowrap">
                      Aufheben
                    </button>
                    <button
                      onClick={exportMarkiert}
                      disabled={markiert.size === 0 || exportLaeuft}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white rounded-lg text-xs font-medium hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap">
                      {exportLaeuft ? <Spinner className="w-4 h-4" /> : <Icon.Download className="w-4 h-4" />}
                      {exportLaeuft ? 'PDF wird erstellt…' : 'Als PDF exportieren'}
                    </button>
                  </div>
                )}
                <MapContainer center={mapCenter} zoom={12} maxZoom={22} style={{ height: '100%', width: '100%' }}>
                  <KartenFokus ziel={kartenFokus} />
                  <KartenGroesse trigger={karteVollbild} />
                  <RechteckAuswahl aktiv={markierModus} onFertig={markiereImRechteck} />
                  {satellit ? (
                    <TileLayer
                      key="satellit"
                      attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                      tileSize={512}
                      zoomOffset={-1}
                      maxNativeZoom={20}
                      maxZoom={22}
                    />
                  ) : (
                    <TileLayer
                      key="osm"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      maxNativeZoom={19}
                      maxZoom={22}
                    />
                  )}
                  {gemeinden && (
                    <WMSTileLayer
                      url="https://opendata.lgln.niedersachsen.de/doorman/noauth/verwaltungsgrenzen_wms"
                      layers="ni_gemeinden_umringe"
                      format="image/png"
                      transparent={true}
                      version="1.3.0"
                      attribution='Gemeindegrenzen &copy; <a href="https://www.lgln.niedersachsen.de">LGLN</a> (dl-de/by-2.0)'
                      maxZoom={22}
                    />
                  )}
                  {alkis && (
                    <WMSTileLayer
                      url="https://opendata.lgln.niedersachsen.de/doorman/noauth/alkis_wms"
                      layers="ALKIS"
                      format="image/png"
                      transparent={true}
                      version="1.3.0"
                      attribution='Flurstücke &copy; <a href="https://www.lgln.niedersachsen.de">LGLN</a> (CC BY 4.0)'
                      maxZoom={22}
                    />
                  )}
                  {gefiltert.filter(i => i.lat && i.lon).map(item => {
                    const sel = markiert.has(item.id);
                    return (
                      <CircleMarker
                        key={item.id}
                        center={[Number(item.lat), Number(item.lon)]}
                        radius={sel ? 9 : 6}
                        pathOptions={sel
                          ? { fillColor: '#163056', color: '#fff', weight: 2.5, fillOpacity: 1 }
                          : { fillColor: item.color ?? '#3b82f6', color: '#fff', weight: 1.5, fillOpacity: 0.85 }}
                        eventHandlers={markierModus ? { click: () => toggleMarkiert(item.id) } : undefined}
                      >
                        {!markierModus && (
                          <PotenzialPopup
                            item={item}
                            onDetails={() => setSelectedItem(item)}
                            onDelete={handleDelete}
                          />
                        )}
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import-Verlauf (nur Admin) */}
      {isAdmin() && importLogOpen && (
        <ImportLogModal log={importLog} onClose={() => setImportLogOpen(false)} />
      )}

      {/* Detail-Modal */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onShowOnMap={it => { setSelectedItem(null); zeigeAufKarte([it]); }}
        />
      )}

      {/* Export-Dialog */}
      {exportOpen && (
        <ExportModal
          items={gefiltert}
          onClose={() => setExportOpen(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
}
